<template>
  <div class="submission-list-page">
    <!-- 标题区域 -->
    <div class="page-header">
      <div class="header-title">
        <i class="el-icon-bot"></i>
        <h2>AI助手</h2>
      </div>
      <p class="header-desc">智能文档生成工具，助力您的内容创作</p>
    </div>

    <!-- 头部筛选区域 -->
    <div class="filter-header">
      <div class="filter-left">
        <el-tabs v-model="activeTab" class="filter-tabs">
          <el-tab-pane label="全部" name="all"></el-tab-pane>
          <el-tab-pane v-if="aiConfig.science !== false" label="科普征文" name="01"></el-tab-pane>
          <el-tab-pane v-if="aiConfig.literature !== false" label="文献解读" name="02"></el-tab-pane>
          <el-tab-pane v-if="aiConfig.caseAnalysis !== false" label="案例解读" name="03"></el-tab-pane>
        </el-tabs>
      </div>
      <div class="filter-right">
        <el-select v-model="statusFilter" placeholder="状态筛选" class="status-filter">
          <el-option label="全部" value=""/>
          <el-option label="生成中" value="waiting"/>
          <el-option label="已生成" value="succeeded"/>
          <el-option label="失败" value="failed"/>
        </el-select>
        <el-button
            type="text"
            icon="el-icon-refresh"
            @click="onRefresh"
            class="refresh-btn"
        >
          刷新
        </el-button>
        <el-button
            v-if="hasEnabledDocType"
            type="primary"
            icon="el-icon-plus"
            @click="openGenerateModal"
            class="add-btn"
        >
          新增任务
        </el-button>
      </div>
    </div>

    <!-- 列表 -->
    <div class="list-wrapper">
      <div
          v-for="item in filteredList"
          :key="item.id"
          class="task-card"
      >
        <!-- 卡片头部 -->
        <div class="card-header">
          <div class="doc-type-badge" :class="'type-' + item.genType">
            {{ getDocTypeLabel(item.genType) }}
          </div>
          <el-tag :type="getStatusTagType(item.genStatus)" size="mini">
            {{ getStatusText(item.genStatus) }}
          </el-tag>
        </div>

        <!-- 卡片内容 -->
        <div class="card-body">
          <h3 class="task-title">{{ item.title || item.keywords || '未命名稿件' }}</h3>
          <p class="task-preview" v-if="item.content">
            {{ stripHtmlAndTruncate(item.content, 120) }}
          </p>
        </div>

        <!-- 卡片底部 -->
        <div class="card-footer">
          <div class="time-info">
            <span class="time-item">
              <i class="el-icon-clock"></i>
              {{ formatDate(item.createTime) }}
            </span>
            <span class="time-divider">|</span>
            <span class="time-item" v-if="item.updateTime">
              <i class="el-icon-check-circle"></i>
              {{ formatDate(item.updateTime) }}
            </span>
          </div>
          <div class="action-buttons">
            <el-button
                v-if="(item.genStatus === 'succeeded' || item.genStatus === '2') && item.content"
                type="text"
                size="mini"
                icon="el-icon-edit"
                @click.stop="editDocument(item)"
                class="action-btn edit-btn"
            >
              编辑
            </el-button>
            <el-button
                v-if="(item.genStatus === 'succeeded' || item.genStatus === '2') && item.resFile"
                type="text"
                size="mini"
                icon="el-icon-download"
                @click.stop="downloadFile(item.resFile)"
                class="action-btn download-btn"
            >
              下载
            </el-button>
            <el-button
                v-if="item.genStatus === 'succeeded' || item.genStatus === '2'"
                type="primary"
                size="mini"
                @click.stop="goAddDocument(item)"
                class="action-btn publish-btn"
            >
              去发布
            </el-button>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <el-empty v-if="filteredList.length === 0" description="暂无记录"/>

      <!-- 无限加载 -->
      <div v-if="filteredList.length > 0" class="load-more">
        <el-spin v-if="loading" tip="加载中..."/>
        <span v-else-if="finished" class="finished-text">没有更多了</span>
      </div>
    </div>

    <!-- 编辑器弹窗 -->
    <el-dialog
        :visible.sync="showEditor"
        width="100%"
        fullscreen
        append-to-body
        title="内容编辑"
        :show-close="false"
    >
      <div class="editor-page">
        <div class="editor-action-bar">
          <el-button size="mini" @click="showEditor = false">返回</el-button>
          <span>字数: {{ wordCount }}</span>
          <el-button type="primary" size="mini" @click="onSave">保存</el-button>
        </div>

        <QuillEditor v-model="content" height="80vh" @on-word-count-change="wordCountChange"/>
      </div>
    </el-dialog>

    <!-- AI生成任务弹窗 -->
    <el-dialog
        title="AI文档生成"
        :visible.sync="showGenerateModal"
        width="720px"
        top="3vh"
        append-to-body
        class="generate-task-dialog"
        @close="showGenerateModal = false"
    >
      <div class="generate-modal-content">
        <GenerateModal @gen-success="handleGenSuccess" @close="handleGenClose"/>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {Message, MessageBox} from 'element-ui'
import {generateList, updateGenerateArticle, getAIConfig} from '@/api/aiassistant'
import GenerateModal from './GenerateModal.vue'
import QuillEditor from '@/components/QuillEditor.vue'

export default {
  name: 'aiassistant',
  components: {
    GenerateModal,
    QuillEditor
  },
  data() {
    return {
      showId: '',
      showEditor: false,
      content: '',
      taskId: '',
      showGenerateModal: false,
      list: [],
      loading: false,
      finished: false,
      refreshing: false,
      wordCount: '',
      activeTab: 'all',
      statusFilter: '',
      aiConfig: {
        science: true,
        literature: true,
        caseAnalysis: true
      }
    }
  },
  computed: {
    filteredList() {
      return this.list.filter(item => {
        // 类型筛选
        if (this.activeTab !== 'all' && item.genType !== this.activeTab) {
          return false
        }
        // 状态筛选
        if (this.statusFilter && item.genStatus !== this.statusFilter) {
          return false
        }
        return true
      })
    },
    hasEnabledDocType() {
      return this.aiConfig.science !== false ||
             this.aiConfig.literature !== false ||
             this.aiConfig.caseAnalysis !== false
    }
  },
  watch: {
    activeTab() {
      // 切换标签时自动刷新
    },
    statusFilter() {
      // 切换状态筛选时自动刷新
    }
  },
  created() {
    this.getGenerateList()
    this.loadAIConfig()
  },
  methods: {
    goAddDocument(item) {
      // 先检查是否有项目ID
      const projectId = localStorage.getItem('projectId')
      if (!projectId) {
        Message.error('请先选择项目')
        return
      }

      // 保存 genId 用于投稿
      localStorage.setItem('genId', item.genId)

      // 触发父组件关闭AI助手并打开投稿弹窗，直接传递完整的item数据
      this.$emit('go-publish', item)
    },

    downloadFile(fileUrl) {
      if (!fileUrl) {
        Message.error('文件不存在')
        return
      }
      const link = document.createElement('a')
      link.href = fileUrl.replace(/`/g, '') // 移除可能的反引号
      link.target = '_blank'
      link.download = ''
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    onSave() {
      MessageBox.confirm(
          '保存后将生成新的文档下载地址，原文档地址将失效。确定要保存吗？',
          '保存确认',
          {
            confirmButtonText: '确定保存',
            cancelButtonText: '取消',
            type: 'warning',
            closeOnClickModal: false
          }
      ).then(() => {
        updateGenerateArticle({
          genId: this.showId,
          content: this.content
        }).then(res => {
          if (res.code === 200) {
            this.getGenerateList()
            Message.success('保存成功，已生成新的文档下载地址')
            this.showEditor = false
          } else {
            Message.error(res.msg || '保存失败')
            this.showEditor = false
          }
        }).catch(() => {
          Message.error('保存失败')
        })
      }).catch(() => {
        Message.info('已取消保存')
      })
    },

    editDocument(item) {
      console.log('editDocument called with item:', item)
      if (!item || !item.content) {
        Message.error('内容不存在')
        return
      }
      this.showId = item.genId
      this.content = item.content
      this.showEditor = true
      console.log('showEditor set to:', this.showEditor)
    },

    wordCountChange(val) {
      this.wordCount = val
    },

    getGenerateList() {
      generateList().then(res => {
        if (res.code === 200) {
          this.list = res.data || this.getMockData()
          this.finished = true
        } else {
          // 如果接口失败，使用模拟数据
          this.list = this.getMockData()
          this.finished = true
        }
      }).catch(() => {
        // 网络异常时使用模拟数据
        this.list = this.getMockData()
        this.finished = true
      })
    },

    getMockData() {
      return [
        {
          id: 1,
          genId: 'GEN20240101001',
          keywords: '糖尿病饮食管理指南',
          genType: '01',
          genStatus: '2',
          content: '糖尿病是一种常见的慢性疾病，合理的饮食管理对于控制血糖至关重要...',
          createTime: '2024-01-15 10:30:00',
          updateTime: '2024-01-15 10:35:00'
        },
        {
          id: 2,
          genId: 'GEN20240101002',
          keywords: '肿瘤免疫治疗新进展',
          genType: '01',
          genStatus: '2',
          content: '近年来，肿瘤免疫治疗取得了突破性进展，为癌症患者带来了新的希望...',
          createTime: '2024-01-14 14:20:00',
          updateTime: '2024-01-14 14:28:00'
        },
        {
          id: 3,
          genId: 'GEN20240101003',
          keywords: '基础总结与梳理',
          genType: '02',
          genStatus: '2',
          content: '本文对近期发表的关于代谢综合征的研究文献进行了系统性总结...',
          createTime: '2024-01-13 09:15:00',
          updateTime: '2024-01-13 09:45:00'
        },
        {
          id: 4,
          genId: 'GEN20240101004',
          keywords: '心血管疾病预防策略',
          genType: '01',
          genStatus: '1',
          content: '',
          createTime: '2024-01-16 16:00:00',
          updateTime: null
        },
        {
          id: 5,
          genId: 'GEN20240101005',
          keywords: '核心结论解读',
          genType: '02',
          genStatus: '2',
          content: '通过对多项临床研究的综合分析，本研究得出以下核心结论...',
          createTime: '2024-01-12 11:30:00',
          updateTime: '2024-01-12 12:00:00'
        }
      ]
    },

    async loadAIConfig() {
      const projectId = localStorage.getItem('projectId')
      if (!projectId) {
        console.log('未找到项目ID，使用默认AI配置')
        return
      }
      try {
        const res = await getAIConfig(projectId)
        if (res.code === 200 && res.data) {
          this.aiConfig = {
            science: res.data.science !== false,
            literature: res.data.literature !== false,
            caseAnalysis: res.data.caseAnalysis !== false
          }
        }
      } catch (err) {
        console.error('加载AI配置失败:', err)
      }
    },


    openGenerateModal() {
      console.log('打开生成任务弹窗')
      this.showGenerateModal = true
    },

    handleGenSuccess(doc) {
      // 生成成功后刷新列表
      this.list.unshift(doc)
      this.showGenerateModal = false
      Message.success('生成成功！')
    },

    handleGenClose() {
      this.showGenerateModal = false
      this.getGenerateList()
    },


    getStatusText(status) {
      const statusMap = {
        'waiting': '排队等待',
        'running': '生成中',
        'succeeded': '成功',
        'failed': '失败',
        '1': '生成中',
        '2': '成功',
        '3': '失败'
      }
      return statusMap[status] || '未知状态'
    },

    getDocTypeLabel(type) {
      const typeMap = {
        '01': '科普征文',
        '02': '文献解读',
        '03': '案例解读'
      }
      return typeMap[type] || '未知类型'
    },

    stripHtmlAndTruncate(html, maxLength) {
      if (!html) return ''
      // 去除 HTML 标签
      const plainText = html.replace(/<[^>]*>/g, '')
      // 去除多余的空白字符
      const cleanedText = plainText.replace(/\s+/g, ' ').trim()
      // 截取指定长度
      if (cleanedText.length <= maxLength) {
        return cleanedText
      }
      return cleanedText.substring(0, maxLength) + '...'
    },

    getStatusTagType(status) {
      const typeMap = {
        'waiting': 'warning',
        'running': 'primary',
        'succeeded': 'success',
        'failed': 'danger',
        '1': 'primary',
        '2': 'success',
        '3': 'danger'
      }
      return typeMap[status] || 'info'
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return '—'
      return date.toLocaleDateString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/\//g, '-')
    },

    onRefresh() {
      this.getGenerateList()
      Message.success('刷新成功')
    },


  }
}
</script>

<style lang="scss" scoped>
.submission-list-page {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  padding: 20px;
  padding-bottom: 90px;
}

/* 页面标题区域 */
.page-header {
  text-align: center;
  margin-bottom: 24px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  backdrop-filter: blur(10px);

  .header-title {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 8px;

    i {
      font-size: 32px;
      color: #fff;
    }

    h2 {
      font-size: 28px;
      font-weight: 700;
      color: #fff;
      margin: 0;
    }
  }

  .header-desc {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
  }
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  padding: 16px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.filter-left {
  flex: 1;
}

.filter-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-filter {
  width: 120px;
}

.filter-tabs {
  ::v-deep .el-tabs__header {
    margin: 0;
  }

  ::v-deep .el-tabs__nav-wrap::after {
    display: none;
  }

  ::v-deep .el-tabs__item {
    font-weight: 500;
    color: #666;
  }

  ::v-deep .el-tabs__item.is-active {
    color: #667eea;
  }

  ::v-deep .el-tabs__active-bar {
    background: #667eea;
  }
}

.refresh-btn {
  margin-bottom: 0;
  color: #667eea;
}

.add-btn {
  margin-bottom: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 6px;
  padding: 8px 20px;

  &:hover {
    opacity: 0.9;
  }
}

/* 任务卡片 */
.task-card {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.doc-type-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;

  &.type-01 {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
  }

  &.type-02 {
    background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
    color: #fff;
  }

  &.type-03 {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    color: #fff;
  }
}

.card-body {
  margin-bottom: 16px;
}

.task-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 10px 0;
  line-height: 1.4;
}

.task-preview {
  font-size: 14px;
  color: #888;
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.time-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #999;

  .time-item {
    display: flex;
    align-items: center;
    gap: 4px;

    i {
      font-size: 12px;
    }
  }

  .time-divider {
    color: #ddd;
  }
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  border-radius: 6px;
  padding: 6px 14px;

  &.edit-btn {
    color: #667eea;
  }

  &.download-btn {
    color: #11998e;
  }

  &.publish-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
  }
}

.load-more {
  text-align: center;
  padding: 16px 0;
}

.finished-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}

.preview-wrapper {
  height: 75vh;
  display: flex;
  flex-direction: column;
}



.preview-iframe {
  flex: 1;
  width: 100%;
  border: none;
}

.editor-page {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.editor-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
}

/* AI生成任务弹窗样式 */
.generate-task-dialog {
  ::v-deep .el-dialog__body {
    padding: 0;
    max-height: 85vh;
    overflow-y: auto;
  }
}

.generate-modal-content {
  padding: 0;
}
</style>
